import type { SkyPromo } from '../api/sky'

/**
 * Regole con cui la pratica Sky propone gli ID promo del canvass: cosa si attiva (SKY_TIPOLOGIA), privato o azienda e la
 * rete di copertura trovata su sky.it (SKY_COPERTURA_RETE) scelgono le promo compatibili; le altre restano scegliibili a
 * mano. Funzioni pure, senza React.
 */
export interface SkyContesto {
  /** SKY_TIPOLOGIA della pratica: TV, FISSO, TV_FISSO, MOBILE, OPZIONE ('' = non ancora scelto). */
  tipologia: string
  /** PRIVATO o AZIENDA ('' = non noto). */
  tipo: string
  /** SKY_COPERTURA_RETE: OPEN_FIBER, FW_FIBERCOP, NON_COPERTO ('' = non ancora verificata). */
  rete: string
}

export const SKY_RETE_LABEL: Record<string, string> = {
  OPEN_FIBER: 'Open Fiber', FW_FIBERCOP: 'FW-Fibercop', TUTTE: 'tutte le coperture', NON_APPLICABILE: '', NON_COPERTO: 'non coperto',
}
const TIPOLOGIA_LABEL: Record<string, string> = { TV: 'TV', FISSO: 'Sky Wifi', TV_FISSO: 'TV + Sky Wifi', ALTRO: '' }
const SEGMENTO_LABEL: Record<string, string> = { RESIDENZIALE: 'residenziale', BUSINESS: 'business', ENTRAMBI: '' }

/** Pagina di Sky per la verifica della copertura (dal canvass: in alternativa My Sales). */
export const SKY_VERIFICA_COPERTURA_URL = 'https://www.sky.it/sky-wifi-fibra/verifica-copertura'

const conLineaFissa = (p: SkyPromo) => p.tipologia === 'FISSO' || p.tipologia === 'TV_FISSO'

/** La promo vale per questa pratica? Una scelta non ancora fatta (valore vuoto) non esclude niente. */
export function promoCompatibile(p: SkyPromo, c: SkyContesto): boolean {
  if (c.tipologia === 'MOBILE') return false
  if (c.tipologia) {
    // Un'opzione su un abbonamento esistente prende le promo TV (pacchetti, Netflix, Glass).
    const t = c.tipologia === 'OPZIONE' ? 'TV' : c.tipologia
    if (p.tipologia !== 'ALTRO' && p.tipologia !== t) return false
  }
  if (c.tipo === 'AZIENDA' && p.segmento === 'RESIDENZIALE') return false
  if (c.tipo === 'PRIVATO' && p.segmento === 'BUSINESS') return false
  if (c.rete === 'NON_COPERTO') return !conLineaFissa(p)
  if (c.rete && (p.rete === 'OPEN_FIBER' || p.rete === 'FW_FIBERCOP') && p.rete !== c.rete) return false
  return true
}

/**
 * Promo compatibili prima (quelle della rete trovata prima di quelle «su tutte le coperture»), poi tutte le altre.
 * Per il resto vale l'ordine del canvass, che è quello delle slide (le offerte principali prima).
 */
export function dividiPromo(list: SkyPromo[], c: SkyContesto): { compatibili: SkyPromo[]; altre: SkyPromo[] } {
  const compatibili: SkyPromo[] = [], altre: SkyPromo[] = []
  for (const p of list) (promoCompatibile(p, c) ? compatibili : altre).push(p)
  const peso = (p: SkyPromo) => (c.rete && p.rete === c.rete ? 0 : 1)
  compatibili.sort((a, b) => peso(a) - peso(b))
  return { compatibili, altre }
}

/** Riga del menu: codice, offerta, variante, rete, prezzo e durata. */
export function etichettaPromo(p: SkyPromo): string {
  const parti = [p.codice, p.offerta]
  if (p.variante) parti.push(p.variante)
  const rete = p.rete === 'OPEN_FIBER' || p.rete === 'FW_FIBERCOP' ? SKY_RETE_LABEL[p.rete] : ''
  if (rete) parti.push(rete)
  if (p.prezzo) parti.push(p.durata ? `${p.prezzo} per ${p.durata}` : p.prezzo)
  return parti.join(' · ')
}

/** AAAA-MM-GG → gg/mm/aaaa (vuoto se non è una data). */
export function dataIt(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
}

/** La promo è scaduta rispetto a oggi (data di Roma, confronto per giorno). */
export function promoScaduta(p: SkyPromo, oggi: Date = new Date()): boolean {
  if (!p.scadenza) return false
  return p.scadenza < isoOggi(oggi)
}

/** Data di oggi in Italia come AAAA-MM-GG. */
export function isoOggi(d: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  return parts
}

/** Data di oggi in Italia come gg/mm/aaaa (default del campo «Data inserimento brand»). */
export function oggiIt(d: Date = new Date()): string {
  return dataIt(isoOggi(d))
}

/** Le condizioni dell'offerta da mostrare sotto il codice scelto, nell'ordine in cui le legge chi carica. */
export function dettagliPromo(p: SkyPromo): [string, string][] {
  const righe: [string, string][] = []
  const add = (k: string, v: string | null | undefined) => { if (v && v.trim()) righe.push([k, v.trim()]) }
  add('Offerta', [p.offerta, p.variante].filter(Boolean).join(' · '))
  add('Per', [TIPOLOGIA_LABEL[p.tipologia] ?? '', SEGMENTO_LABEL[p.segmento] ?? ''].filter(Boolean).join(', '))
  add('Copertura', p.rete === 'TUTTE' ? 'tutte le coperture' : p.rete === 'NON_APPLICABILE' ? '' : SKY_RETE_LABEL[p.rete])
  add('Include', p.componenti.join(', '))
  add('Prezzo', p.prezzo && p.durata ? `${p.prezzo} per ${p.durata}` : p.prezzo)
  add('Dopo', p.dopo)
  add('Attivazione', p.attivazione)
  add('Vincolo', p.vincolo)
  add('Scadenza', dataIt(p.scadenza))
  add('Note', p.note.join(' · '))
  if (p.pagina) add('Canvass', `pagina ${p.pagina}`)
  return righe
}

/** L'indirizzo dove si attiva la linea (quello di attivazione se diverso, altrimenti la residenza), per la verifica su sky.it. */
export function indirizzoAttivazione(v: Record<string, string>): string {
  const s = v['ATTIVAZIONE_DIVERSA'] === 'SI' && (v['INDIRIZZO_ATTIVAZIONE'] ?? '').trim() ? 'ATTIVAZIONE' : 'CLIENTE'
  const via = (v[`INDIRIZZO_${s}`] ?? '').trim(), civico = (v[`NUM_CIVICO_${s}`] ?? '').trim()
  const comune = (v[s === 'CLIENTE' ? 'ID_COMUNE_CLIENTE' : 'ID_COMUNE_ATTIVAZIONE'] ?? '').trim()
  return [via && civico ? `${via} ${civico}` : via, comune].filter(Boolean).join(', ')
}
